import React, { useState } from "react";
import Page, { PageBreadcrumbs } from "~/components/Page";
import styles from "./styles.module.scss";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Home } from "lucide-react";
import { Link } from "react-router-dom";
import paths from "~/paths";
import TabContent from "~/pages/PersonalArea/TabContent";

type Props = {
  breadcrumbsItems: {
    href?: string;
    label: string;
  }[];
  tabs: { id: string; label: string; render: (selected: boolean) => React.ReactNode }[];
  onTabChange?: (tabId: string) => void;
  defaultSelectedTab?: number;
  pageTitle: string;
  withTabContentBackground?: boolean;
  withTabContentPadding?: boolean;
};

const TabsPageView = (props: Props) => {
  const {
    breadcrumbsItems,
    tabs,
    defaultSelectedTab,
    pageTitle,
    onTabChange,
    withTabContentBackground = true,
    withTabContentPadding = true,
  } = props;
  const [selectedId, setSelectedId] = useState(tabs[defaultSelectedTab || 0]?.id ?? tabs[0].id);

  const onChange = (id: string) => {
    setSelectedId(id);
    onTabChange?.(id);
  };

  return (
    <Page title={pageTitle} className={styles.page}>
      {tabs.length && (
        <PageBreadcrumbs>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={paths.construct.index()}>
                    <Home className="size-4" />
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbsItems.map((item) => (
                <React.Fragment key={`breadcrumb-${item.label}`}>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {item.href ? (
                      <BreadcrumbLink asChild>
                        <Link to={item.href}>{item.label}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{item.label}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </PageBreadcrumbs>
      )}

      <div className={styles.header}>
        <h1 className="text-2xl font-semibold">{pageTitle}</h1>
      </div>

      <div className={styles.tabs}>
        <Tabs value={selectedId} onValueChange={onChange}>
          <TabsList variant="line" className="mx-6">
            {tabs.map((tab) => (
              <TabsTrigger key={`tab-${tab.id}`} value={tab.id}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((tab) => (
            <TabsContent key={`tabc-${tab.id}`} value={tab.id}>
              <TabContent withBackground={withTabContentBackground} withPadding={withTabContentPadding}>
                {tab.render(selectedId === tab.id)}
              </TabContent>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Page>
  );
};

export default TabsPageView;
